import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Headphones, Music, Mic2, Video, ArrowRight, Check, Loader2 } from 'lucide-react';
import { API } from '../../config/api';



const INTEREST_OPTIONS = [
  { id: 'music', label: 'Music & New Releases', icon: Music },
  { id: 'podcasts', label: 'Podcasts & Interviews', icon: Mic2 },
  { id: 'battles', label: 'Battles & Cyphers', icon: Headphones },
  { id: 'events', label: 'Events & Live Shows', icon: Video }
];

export default function ListenerOnboarding() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user?.role && user.role !== 'listener' && user.role !== 'owner') {
      navigate(`/${user.role}/dashboard`);
    }
  }, [isAuthenticated, authLoading, user]);

  const toggleInterest = (interestId) => {
    setInterests(prev => 
      prev.includes(interestId) 
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    );
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      // Save interests to listener profile
      await fetch(`${API}/listener/preferences`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ interests })
      });
      
      await refreshUser();
      toast.success('Welcome to DMV Soundstage!');
      navigate('/listener/dashboard');
    } catch (err) {
      console.error('Onboarding error:', err);
      navigate('/listener/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/listener/dashboard');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="listener-onboarding">
      <div className="max-w-lg mx-auto">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-secondary' : 'bg-muted/50'}`} />
          <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-secondary' : 'bg-muted/50'}`} />
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-gray-500/20 flex items-center justify-center mx-auto mb-6">
              <Headphones className="w-10 h-10 text-gray-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">Welcome, {user?.name}!</h1>
            <p className="text-muted-foreground mb-8">
              You're now part of the DMV Soundstage community. Let's personalize your experience.
            </p>
            <Button onClick={() => setStep(2)} size="lg">
              Let's Go <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Interests */}
        {step === 2 && (
          <div>
            <h1 className="font-heading text-2xl font-bold mb-2">What interests you?</h1>
            <p className="text-muted-foreground mb-6">Select all that apply. We'll show you relevant content.</p>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {INTEREST_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = interests.includes(option.id);
                
                return (
                  <Card
                    key={option.id}
                    className={`p-4 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-secondary bg-secondary/10' 
                        : 'border-border hover:border-border'
                    }`}
                    onClick={() => toggleInterest(option.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-secondary/20' : 'bg-muted/30'
                      }`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-secondary' : 'text-muted-foreground'}`} />
                      </div>
                      <span className="text-sm font-medium">{option.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-secondary ml-auto" />}
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSkip} className="flex-1">
                Skip for now
              </Button>
              <Button onClick={handleComplete} disabled={loading} className="flex-1">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Continue
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
